/**
 * 关于面板（DESIGN.md §13.1、§1.4）。
 *
 * 两件事必须能在应用里直接看到，而不是只写在仓库文件里：
 * 1. **第三方许可证清单**——AGPL 项目的合规要求，用户有权知道自己在运行什么。
 * 2. **隐私承诺**——§1.4 要求"首屏就说清楚"，并且是可验证的说法而非广告词。
 *
 * 清单用 `?raw` 在构建期内联 `THIRD_PARTY_LICENSES.md`，因此它与仓库里那份
 * **永远是同一份**：CI 校验清单与真实依赖一致（`pnpm licenses:check`），
 * 这里又直接读那个文件，中间没有可以走样的环节。
 */
import { createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import licensesMarkdown from '../../../THIRD_PARTY_LICENSES.md?raw';
import { useFocusTrap } from '../a11y/focus-trap';
import styles from './AboutDialog.module.css';

export interface LicenseEntry {
  readonly ecosystem: string;
  readonly name: string;
  readonly version: string;
  readonly license: string;
}

/**
 * 从清单 Markdown 里解析出依赖表。
 *
 * 只解析生成段（标记之间的部分）：人写的说明段落里也有表格（例外表），
 * 混进来会让"共 N 项依赖"这个数字不准。
 */
export function parseLicenses(markdown: string): LicenseEntry[] {
  const begin = markdown.indexOf('<!-- BEGIN GENERATED LICENSES -->');
  const end = markdown.indexOf('<!-- END GENERATED LICENSES -->');
  if (begin < 0 || end < 0) return [];

  const rows = markdown.slice(begin, end).split('\n');
  const entries: LicenseEntry[] = [];
  for (const row of rows) {
    const cells = row.split('|').map((c) => c.trim());
    // | 生态 | 依赖 | 版本 | 许可证 | → 首尾为空串，中间四格
    if (cells.length !== 6) continue;
    const [, ecosystem, name, version, license] = cells;
    if (ecosystem === undefined || name === undefined) continue;
    if (ecosystem === '生态' || ecosystem.startsWith('--')) continue;
    entries.push({
      ecosystem,
      name,
      version: version ?? '',
      license: license ?? '',
    });
  }
  return entries;
}

export interface AboutDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function AboutDialog(props: AboutDialogProps): JSX.Element {
  const [filter, setFilter] = createSignal('');
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);
  const all = createMemo(() => parseLicenses(licensesMarkdown));
  const shown = createMemo(() => {
    const q = filter().trim().toLowerCase();
    if (q === '') return all();
    return all().filter(
      (e) => e.name.toLowerCase().includes(q) || e.license.toLowerCase().includes(q),
    );
  });

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="about-backdrop">
        <div
          ref={(el) => {
            dialogRef = el;
          }}
          class={styles.dialog}
          role="dialog"
          aria-label="关于 Photosmith"
          data-testid="about-dialog"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <header class={styles.header}>
            <h1 class={styles.title}>Photosmith</h1>
            <button
              type="button"
              class={styles.close}
              onClick={props.onClose}
              aria-label="关闭"
              data-testid="about-close"
            >
              ✕
            </button>
          </header>

          <section class={styles.section}>
            <h2 class={styles.sectionTitle}>隐私承诺</h2>
            <p class={styles.prose}>
              你的图片不会离开这台设备。首屏加载完成后本应用不发出任何网络请求，
              这一点由内容安全策略（<code>connect-src 'self'</code>）在浏览器层面强制，
              并有自动化测试持续验证。
            </p>
            <p class={styles.hint}>不信可以打开开发者工具的网络面板。</p>
          </section>

          <section class={styles.section}>
            <h2 class={styles.sectionTitle}>许可</h2>
            <p class={styles.prose}>
              本程序以 AGPL-3.0-or-later 分发。
            </p>
          </section>

          <section class={styles.section}>
            <div class={styles.licenseHeader}>
              <h2 class={styles.sectionTitle}>
                第三方依赖（<span data-testid="license-count">{shown().length}</span> / {all().length}）
              </h2>
              <input
                class={styles.filter}
                type="search"
                placeholder="筛选依赖或许可证"
                value={filter()}
                onInput={(e) => {
                  setFilter(e.currentTarget.value);
                }}
                data-testid="license-filter"
              />
            </div>
            <div class={styles.tableWrap}>
              <table class={styles.table} data-testid="license-table">
                <thead>
                  <tr>
                    <th>依赖</th>
                    <th>版本</th>
                    <th>许可证</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={shown()}>
                    {(entry) => (
                      <tr>
                        <td>
                          <span class={styles.ecosystem}>{entry.ecosystem}</span>
                          {entry.name}
                        </td>
                        <td class={styles.version}>{entry.version}</td>
                        <td>{entry.license}</td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </Show>
  );
}
